<script setup lang="ts">
  import { DocumentsDialog } from '@/components/app/documents'
  import EmptyChatFolderIllustration from '@/components/svg/Illustrations/EmptyChatFolderIllustration.vue'
  import { Button } from '@/components/ui/button'
  import { useTranslation } from '@/composables/useTranslation'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import { type AIChatSession, type AiChatQuestionTemplate } from '@/types/generated'
  import { AiChatSessionGroupsService } from '@/types/generated/services.gen'
  import type { SessionGroup } from '@/types/generated/types.gen'
  import {
    Check,
    Eye,
    FilePlus,
    FolderOpen,
    MessageCirclePlus,
    MessageSquare,
    Pencil,
    PencilRuler,
    FileText
  } from 'lucide-vue-next'
  import { computed, nextTick, ref, watch } from 'vue'
  import { genericErrorToast } from '@/lib/toasts'
  import InstructionsDialog from '../../components/InstructionsDialog.vue'

  interface Props {
    folder: SessionGroup
  }

  const props = defineProps<Props>()

  // Editing state
  const isEditingName = ref(false)
  const editName = ref('')
  const inputRef = ref<HTMLInputElement>()
  const maxCharacters = 50

  // Computed property for character count
  const characterCount = computed(() => editName.value.length)

  const emit = defineEmits<{
    editFolder: [folder: SessionGroup]
    newChat: [folder: SessionGroup]
    folderUpdated: [folder: SessionGroup]
    selectChat: [session: AIChatSession]
    usePrompt: [prompt: AiChatQuestionTemplate]
  }>()

  const { t } = useTranslation({
    en: {
      folderName: 'Folder name',
      editFolder: 'Edit folder',
      addDocuments: 'Add Documents',
      addDocumentsDescription: 'Chats in this project can access file content',
      addInstructions: 'Add Instructions',
      addInstructionsDescription: 'Tailor the way AI responds in this project',
      askQuestions: 'Ask your questions...',
      uploadDocuments: 'Upload documents',
      promptTemplates: 'Prompt templates',
      send: 'Send',
      chats: 'Chats',
      prompts: 'Prompts',
      emptyFolderTitle: 'No chats yet in this folder.',
      newChat: 'New Chat',
      QuickActions: 'Quick Actions',
      UploadDocumentsToFolder: 'Upload documents to the folder',
      AllUploadedDocumentsAttachedToFolder:
        'All uploaded documents are attached to the folder and are available in all AI chats within the folder.'
    },
    de: {
      folderName: 'Ordnername',
      editFolder: 'Ordner bearbeiten',
      addDocuments: 'Dokumente hinzufügen',
      addDocumentsDescription: 'Chats in diesem Ordner haben Zugriff auf alle Dateiinhalte.',
      addInstructions: 'Anweisungen hinzufügen',
      addInstructionsDescription: 'Passen Sie die Art an, wie KI in diesem Projekt antwortet',
      askQuestions: 'Stellen Sie Ihre Fragen...',
      uploadDocuments: 'Dokumente hochladen',
      promptTemplates: 'Prompt-Vorlagen',
      send: 'Senden',
      chats: 'Chats',
      prompts: 'Prompts',
      emptyFolderTitle: 'Noch keine Chats in diesem Ordner.',
      newChat: 'Neuer Chat',
      QuickActions: 'Schnell Aktionen',
      UploadDocumentsToFolder: 'Dokumente in den Ordner hochladen',
      AllUploadedDocumentsAttachedToFolder:
        'Alle hochgeladenen Dokumente sind an den Ordner angehängt und stehen in allen KI-Chats innerhalb des Ordners zur Verfügung.'
    }
  })

  const localFolder = ref<SessionGroup>({ ...props.folder })
  const showInstructionsDialog = ref(false)
  const showPromptTemplatesDialog = ref(false)
  const showUploadDocumentsDialog = ref(false)

  // Use the centralized folder processor from the store
  const globalChatStore = useGlobalChatStore()
  const folderDocumentProcessor = computed(() =>
    globalChatStore.getFolderProcessor(localFolder.value.uuid)
  )

  const folderDocuments = ref<any[]>([])

  // Chat sessions management
  const folderChatSessions = ref<AIChatSession[]>([])
  const folderPrompts = ref<AiChatQuestionTemplate[]>([])

  // Single contents fetch — splits the mixed response by type to avoid a second round-trip.
  const loadFolderContents = async () => {
    try {
      const res = await AiChatSessionGroupsService.aiChatSessionGroupsContentsList({
        uuid: localFolder.value.uuid
      })
      folderChatSessions.value = res.filter(
        (item) => item.type === 'chat'
      ) as unknown as AIChatSession[]
      folderPrompts.value = res.filter(
        (item) => item.type === 'prompt'
      ) as unknown as AiChatQuestionTemplate[]
    } catch (error) {
      genericErrorToast(error)
      folderChatSessions.value = []
      folderPrompts.value = []
    }
  }

  // Load documents for this folder
  const loadFolderDocuments = async () => {
    try {
      // Get documents from the session group
      if (localFolder.value.documents && Array.isArray(localFolder.value.documents)) {
        folderDocuments.value = localFolder.value.documents
      } else {
        folderDocuments.value = []
      }
    } catch (error) {
      console.error('Failed to load folder documents:', error)
      folderDocuments.value = []
    }
  }

  // Computed property for file count
  const fileCount = computed(() => folderDocuments.value.length)
  const hasDocuments = computed(() => fileCount.value > 0)

  // Watch for folder prop changes and update local state
  watch(
    () => props.folder.uuid, // Only watch UUID changes, not deep object changes
    (newUuid, oldUuid) => {
      // Only update if the folder UUID actually changed
      if (oldUuid && newUuid !== oldUuid) {
        localFolder.value = { ...props.folder }
        loadFolderDocuments() // Reload documents when folder changes
        loadFolderContents()
      } else if (!oldUuid) {
        // Initial load
        localFolder.value = { ...props.folder }
        loadFolderDocuments()
        loadFolderContents()
      }
    },
    { immediate: true }
  )

  watch(
    () => [
      props.folder.name,
      props.folder.description,
      props.folder.instructions,
      props.folder.documents
    ],
    (
      [newName, newDescription, newInstructions, newDocuments],
      [oldName, oldDescription, oldInstructions, oldDocuments]
    ) => {
      // Only update if it's the same folder (same UUID) and properties actually changed
      if (
        localFolder.value.uuid === props.folder.uuid &&
        (newName !== oldName ||
          newDescription !== oldDescription ||
          newInstructions !== oldInstructions ||
          newDocuments !== oldDocuments)
      ) {
        localFolder.value = { ...props.folder }
        // Reload documents if they changed
        if (newDocuments !== oldDocuments) {
          loadFolderDocuments()
        }
      }
    }
  )

  const handleAddInstructions = () => {
    showInstructionsDialog.value = true
  }

  const handleNewChat = () => {
    emit('newChat', localFolder.value)
  }

  const handleInstructionsSave = async (instructions: string) => {
    try {
      // Update the folder with new instructions via API
      await globalChatStore.updateSessionGroup(localFolder.value.uuid, {
        name: localFolder.value.name,
        description: localFolder.value.description,
        instructions: instructions
      })

      // Close the dialog
      showInstructionsDialog.value = false
    } catch (error) {
      console.error('Failed to save instructions:', error)
    }
  }

  const handleInstructionsClose = () => {
    showInstructionsDialog.value = false
  }

  // Handle dialog close for DocumentsDialog
  const handleDocumentsDialogClose = async () => {
    // Refresh the session group to get updated documents when dialog closes
    try {
      await globalChatStore.refreshSessionGroup(localFolder.value.uuid)
    } catch (error) {
      console.error('Failed to refresh session group:', error)
    }
  }

  const handleDocumentDeleted = async (documentId: string) => {
    console.log('Document deleted:', documentId)
    // Refresh the session group to get updated documents
    try {
      await globalChatStore.refreshSessionGroup(localFolder.value.uuid)
    } catch (error) {
      console.error('Error refreshing session group:', error)
    }
  }

  const handleUploadComplete = async () => {
    console.log('Upload complete')
  }

  const startEdit = () => {
    isEditingName.value = true
    editName.value = localFolder.value.name
    nextTick(() => {
      inputRef.value?.focus()
      inputRef.value?.select()
    })
  }

  const saveEdit = async () => {
    const trimmedName = editName.value.trim()
    if (trimmedName && trimmedName !== localFolder.value.name) {
      // Validate character limit
      if (trimmedName.length > maxCharacters) {
        console.warn(`Folder name must be less than ${maxCharacters} characters`)
        return
      }

      try {
        const updatedFolder = await globalChatStore.updateSessionGroup(localFolder.value.uuid, {
          name: trimmedName,
          description: localFolder.value.description,
          instructions: localFolder.value.instructions
        })
        localFolder.value = updatedFolder
        emit('folderUpdated', updatedFolder)
      } catch (error) {
        console.error('Failed to update folder name:', error)
      }
    }
    isEditingName.value = false
  }

  const cancelEdit = () => {
    isEditingName.value = false
    editName.value = localFolder.value.name
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveEdit()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelEdit()
    }
  }

  const recentChatSessions = computed(() => {
    return folderChatSessions.value
  })

  const getUserInitials = (userUuid: string) => {
    if (!userUuid) return 'U'

    const user = globalChatStore.getUserByUuid(userUuid)

    if (user) {
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase() || 'U'
    }

    // Fallback to first letter of UUID
    return userUuid.charAt(0).toUpperCase() || 'U'
  }

  const getUserName = (userUuid: string) => {
    if (!userUuid) return 'Unknown User'

    const user = globalChatStore.getUserByUuid(userUuid)

    if (user) {
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      return `${firstName} ${lastName}`.trim() || 'Unknown User'
    }

    // Fallback to generic name
    return 'User'
  }

  const getUserAvatar = (userUuid: string) => {
    if (!userUuid) return null

    const user = globalChatStore.getUserByUuid(userUuid)

    if (user && user.avatar) {
      return user.avatar
    }

    return null
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })
  }

  const handleSelectChat = (session: AIChatSession) => {
    emit('selectChat', session)
  }

  const handleUsePrompt = (prompt: AiChatQuestionTemplate) => {
    emit('usePrompt', prompt)
  }

  const handlePreviewChat = (session: AIChatSession) => {
    emit('selectChat', session)
  }
</script>

<template>
  <div
    class="flex h-full w-full flex-col items-center justify-center gap-4 bg-gray-50 p-8"
    data-cy="folder-view"
  >
    <!-- Folder Header -->
    <div
      class="flex w-full max-w-2xl items-center gap-4 rounded-lg bg-gray-100 p-3"
      data-cy="folder-header"
    >
      <!-- Folder Icon -->
      <div class="flex h-6 w-6 items-center justify-center">
        <FolderOpen class="h-6 w-6 text-gray-800" />
      </div>

      <!-- Edit Mode -->
      <div
        v-if="isEditingName"
        class="flex flex-1 flex-col gap-1"
      >
        <div class="flex flex-1 items-center gap-1">
          <input
            ref="inputRef"
            v-model="editName"
            class="flex-1 rounded-md border border-purple-500 bg-white px-2 py-1.5 text-sm font-normal text-gray-800
              outline-none focus:border-purple-500 focus:ring-0"
            placeholder="Folder name"
            :maxlength="maxCharacters"
            @keydown="handleKeydown"
            @blur="saveEdit"
          />
          <button
            class="size-6 shrink-0 cursor-pointer"
            @click="saveEdit"
          >
            <Check class="h-6 w-6 text-gray-800" />
          </button>
        </div>
        <div class="text-right text-xs text-gray-500">
          {{ characterCount }}/{{ maxCharacters }} characters
        </div>
      </div>

      <!-- Default Mode -->
      <div
        v-else
        class="flex flex-1 items-center justify-between"
      >
        <h2
          class="text-lg font-medium text-gray-800"
          data-cy="folder-name"
        >
          {{ localFolder.name }}
        </h2>
        <div class="flex items-center gap-2">
          <button
            class="flex h-6 w-6 items-center justify-center"
            title="Edit folder name"
            data-cy="edit-folder-button"
            @click="startEdit"
          >
            <Pencil class="h-5 w-5 text-gray-800" />
          </button>
        </div>
      </div>
    </div>

    <!-- Action Cards -->
    <div
      class="w-full max-w-2xl"
      data-cy="quick-actions-section"
    >
      <h3
        class="mb-3 text-sm font-medium text-gray-600"
        data-cy="quick-actions-title"
      >
        {{ t('QuickActions') }}
      </h3>
      <div
        class="flex flex-row gap-3"
        data-cy="quick-actions-grid"
      >
        <!-- Upload Documents Card -->
        <DocumentsDialog
          v-if="localFolder?.uuid"
          :document-processor="folderDocumentProcessor"
          :title="t('UploadDocumentsToFolder')"
          :note="t('AllUploadedDocumentsAttachedToFolder')"
          @document-deleted="handleDocumentDeleted"
          @upload-complete="handleUploadComplete"
          @dialog-close="handleDocumentsDialogClose"
        >
          <template #trigger>
            <button
              class="flex flex-1 flex-col gap-2 rounded-lg bg-white p-3 shadow-sm hover:bg-gray-50"
              data-cy="add-documents-card"
            >
              <div class="flex items-center gap-2">
                <FilePlus class="h-5 w-5 text-gray-800" />
                <h4 class="text-sm font-medium text-gray-800">{{ t('addDocuments') }}</h4>
              </div>
              <p class="text-left text-xs text-gray-500">
                {{ hasDocuments ? `${fileCount} Files` : t('addDocumentsDescription') }}
              </p>
            </button>
          </template>
        </DocumentsDialog>

        <!-- Add Instructions Card -->
        <button
          class="flex flex-1 flex-col gap-2 rounded-lg bg-white p-3 shadow-sm hover:bg-gray-50"
          data-cy="add-instructions-card"
          @click="handleAddInstructions"
        >
          <div class="flex items-center gap-2">
            <PencilRuler class="h-5 w-5 text-gray-800" />
            <h4 class="text-sm font-medium text-gray-800">{{ t('addInstructions') }}</h4>
          </div>
          <p class="text-left text-xs text-gray-500">
            {{ t('addInstructionsDescription') }}
          </p>
        </button>
      </div>
    </div>

    <!-- Empty State (when no chats) -->
    <div
      v-if="recentChatSessions.length === 0"
      class="flex w-full max-w-2xl flex-col items-center justify-center gap-4 py-6"
    >
      <EmptyChatFolderIllustration />
      <p class="text-base font-medium text-gray-800">
        {{ t('emptyFolderTitle') }}
      </p>
      <Button
        class="bg-purple-600 hover:bg-purple-700"
        data-cy="new-chat-button"
        @click="handleNewChat"
      >
        <MessageSquare class="mr-2 h-4 w-4" />
        {{ t('newChat') }}
      </Button>
    </div>

    <!-- Chats Section (when chats exist) -->
    <div
      v-else
      class="w-full max-w-2xl"
    >
      <div class="mb-2 flex items-center gap-2">
        <h3 class="text-base font-medium text-gray-800">{{ t('chats') }}</h3>
        <div class="flex-1 border-t border-gray-200"></div>
        <Button
          size="sm"
          class="h-10 bg-purple-600 shadow-sm hover:bg-purple-700"
          data-cy="new-chat-button"
          @click="handleNewChat"
        >
          <MessageCirclePlus class="mr-2 h-4 w-4" />
          {{ t('newChat') }}
        </Button>
      </div>

      <!-- Chat List -->
      <div
        class="max-h-96 space-y-1 overflow-y-auto"
        data-cy="folder-chat-sessions"
      >
        <div
          v-for="session in recentChatSessions"
          :key="session.uuid"
          class="flex cursor-pointer items-center gap-2 rounded-lg p-3 transition-colors hover:bg-blue-50
            hover:shadow-sm"
          data-cy="folder-chat-session-item"
          @click="handleSelectChat(session)"
        >
          <!-- Avatar -->
          <div class="relative h-8 w-8 shrink-0">
            <div
              v-if="getUserAvatar(session.started_by_user)"
              class="h-8 w-8 rounded-full bg-cover bg-center"
              :style="{ backgroundImage: `url(${getUserAvatar(session.started_by_user)})` }"
            />
            <div
              v-else
              class="flex h-8 w-8 items-center justify-center rounded-full bg-blue-500"
            >
              <span class="text-sm font-semibold text-white">
                {{ getUserInitials(session.started_by_user) }}
              </span>
            </div>
          </div>

          <!-- Chat Info -->
          <div class="flex flex-1 flex-col">
            <div class="flex items-baseline justify-between">
              <span class="text-sm font-semibold text-gray-800">
                {{ getUserName(session.started_by_user) }}
              </span>
              <span class="text-xs text-gray-400">
                {{ formatDate(session.created_at) }}
              </span>
            </div>
            <span class="truncate text-sm text-gray-500">
              {{ session.title }}
            </span>
          </div>

          <!-- Preview Button -->
          <button
            class="flex h-8 w-8 items-center justify-center rounded-md bg-white shadow-sm hover:bg-gray-50"
            @click.stop="handlePreviewChat(session)"
          >
            <Eye class="h-4 w-4 text-gray-600" />
          </button>
        </div>
      </div>
    </div>

    <!-- Prompts Section (when prompts exist in folder) -->
    <div
      v-if="folderPrompts.length > 0"
      class="w-full max-w-2xl"
    >
      <div class="mb-2 flex items-center gap-2">
        <h3 class="text-base font-medium text-gray-800">{{ t('prompts') }}</h3>
        <div class="flex-1 border-t border-gray-200"></div>
      </div>
      <div
        class="max-h-60 space-y-1 overflow-y-auto"
        data-cy="folder-prompt-templates"
      >
        <div
          v-for="prompt in folderPrompts"
          :key="prompt.uuid"
          class="flex cursor-pointer items-center gap-2 rounded-lg p-3 transition-colors hover:bg-purple-50
            hover:shadow-sm"
          data-cy="folder-prompt-item"
          @click="handleUsePrompt(prompt)"
        >
          <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-100">
            <FileText class="h-4 w-4 text-purple-600" />
          </div>
          <div class="flex flex-1 flex-col">
            <span class="text-sm font-semibold text-gray-800">{{ prompt.title }}</span>
            <span
              v-if="prompt.description"
              class="truncate text-xs text-gray-500"
            >
              {{ prompt.description }}
            </span>
          </div>
          <span
            class="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700"
          >
            {{ t('prompts') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Instructions Dialog -->
    <InstructionsDialog
      :open="showInstructionsDialog"
      :folder="localFolder"
      :initial-instructions="localFolder.instructions"
      @close="handleInstructionsClose"
      @save="handleInstructionsSave"
    />
  </div>
</template>
